import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, Control } from 'react-hook-form';
import { EntityTypeSelector, EntityType } from './EntityTypeSelector';
import {
  SearchableCombobox,
  createSupplierOptions,
  createCustomerOptions,
  ComboboxOption,
} from '../UI/SearchableCombobox';
import { Supplier, Customer, Warehouse } from '../../types/entities';
import { MapPin, User, Phone } from 'lucide-react';

interface PickupDeliveryData {
  type: EntityType;
  id: number | null;
  address: string;
  contactPerson: string;
  contactPhone: string;
}

interface PickupDeliverySectionProps {
  label: 'pickup' | 'delivery';
  control: Control<any>;
  data: PickupDeliveryData;
  onTypeChange: (type: EntityType) => void;
  onEntitySelect: (entity: any) => void;
  suppliers: Supplier[];
  customers: Customer[];
  warehouses: Warehouse[];
  disabled?: boolean;
}

// Create warehouse options for combobox
const createWarehouseOptions = (warehouses: Warehouse[]): ComboboxOption[] => {
  return warehouses.map((w) => ({
    id: w.id,
    label: w.name,
    sublabel: w.address || '',
    searchFields: [w.name, w.code || '', w.address || ''],
  }));
};

export const PickupDeliverySection: React.FC<PickupDeliverySectionProps> = ({
  label,
  control,
  data,
  onTypeChange,
  onEntitySelect,
  suppliers,
  customers,
  warehouses,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const isPickup = label === 'pickup';
  const fieldPrefix = isPickup ? 'pickup' : 'delivery';

  // Create options based on entity type
  const entityOptions = useMemo(() => {
    switch (data.type) {
      case 'SUPPLIER':
        return createSupplierOptions(suppliers);
      case 'CUSTOMER':
        return createCustomerOptions(customers);
      case 'WAREHOUSE':
        return createWarehouseOptions(warehouses);
      default:
        return [];
    }
  }, [data.type, suppliers, customers, warehouses]);

  // Get placeholder text based on entity type
  const getPlaceholder = () => {
    switch (data.type) {
      case 'SUPPLIER':
        return t('trips.selectSupplier');
      case 'CUSTOMER':
        return t('trips.selectCustomer');
      case 'WAREHOUSE':
        return t('trips.selectWarehouse');
      default:
        return t('common.select');
    }
  };

  // Handle entity selection
  const handleEntityChange = (entityId: number | string | null) => {
    if (entityId === null) {
      onEntitySelect(null);
      return;
    }

    let entity = null;
    switch (data.type) {
      case 'SUPPLIER':
        entity = suppliers.find((s) => s.id === entityId);
        break;
      case 'CUSTOMER':
        entity = customers.find((c) => c.id === entityId);
        break;
      case 'WAREHOUSE':
        entity = warehouses.find((w) => w.id === entityId);
        break;
    }

    if (entity) {
      onEntitySelect(entity);
    }
  };

  return (
    <div className="card mb-4">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <MapPin className="w-5 h-5" />
        {isPickup ? t('trips.pickupPoint') : t('trips.deliveryPoint')}
      </h3>

      {/* Entity Type Selector */}
      <EntityTypeSelector
        value={data.type}
        onChange={onTypeChange}
        disabled={disabled}
      />

      {/* Entity Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          {data.type === 'SUPPLIER' && t('trips.supplier')}
          {data.type === 'CUSTOMER' && t('trips.customer')}
          {data.type === 'WAREHOUSE' && t('warehouses.title')}
          <span className="text-red-500"> *</span>
        </label>
        <Controller
          name={`${fieldPrefix}.id`}
          control={control}
          rules={{ required: t('validation.required') }}
          render={({ field }) => (
            <SearchableCombobox
              options={entityOptions}
              value={field.value}
              onChange={(val) => {
                field.onChange(val);
                handleEntityChange(val);
              }}
              placeholder={getPlaceholder()}
              required
            />
          )}
        />
      </div>

      {/* Address Info (auto-filled or manual) */}
      {data.id && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {t('common.address')}
              </label>
              <Controller
                name={`${fieldPrefix}.address`}
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    className="input-field text-sm"
                    placeholder={t('trips.addressPlaceholder')}
                    disabled={disabled}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-gray-500 mt-0.5" />
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">
                  {t('trips.contactPerson')}
                </label>
                <Controller
                  name={`${fieldPrefix}.contactPerson`}
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="input-field text-sm"
                      placeholder={t('trips.contactPersonPlaceholder')}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Phone className="w-4 h-4 text-gray-500 mt-0.5" />
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">
                  {t('trips.contactPhone')}
                </label>
                <Controller
                  name={`${fieldPrefix}.contactPhone`}
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="input-field text-sm"
                      placeholder={t('trips.contactPhonePlaceholder')}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
